import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Save, Pencil } from "lucide-react";
import Avatar from "@/components/Avatar";
import { useConfirm } from "@/components/ConfirmDialog";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };
const ROLES = ["org_admin", "producer", "production_staff", "support_staff", "cast"];

/**
 * Extracted from Users.jsx CompanySection so the roster can be reused inside
 * the People hub without dragging along the production-settings surface.
 */
export default function CompanyRoster() {
  const { user, productionId } = useAuth();
  const isAdmin = user.role === "org_admin";
  const confirm = useConfirm();
  const [members, setMembers] = useState([]);
  const [productionRoles, setProductionRoles] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "cast", phone: "", character: "", production_role: "" });
  const [editUser, setEditUser] = useState(null);

  const load = async () => {
    if (!productionId) return;
    const r = await api.get(`/productions/${productionId}/members`);
    setMembers(r.data);
  };
  useEffect(() => {
    load();
    api.get("/production-roles").then((r) => setProductionRoles(r.data.defaults || [])).catch(() => {});
  }, [productionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Staff callers can only mint cast members — the role dropdown reflects that.
  const availableRoles = isAdmin ? ROLES : ["cast"];

  const create = async () => {
    if (!form.email || !form.password || !form.name) return toast.error("Email, password and name required");
    try {
      await api.post("/users", { ...form, production_id: productionId });
      setOpenNew(false);
      setForm({ email: "", password: "", name: "", role: "cast", phone: "", character: "", production_role: "" });
      load();
      toast.success("User created & added to production");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    }
  };
  const removeMember = async (userId) => {
    if (userId === user.id) return toast.error("You can't remove yourself.");
    const ok = await confirm({ title: "Remove from production?", body: "The user keeps their account but loses access to this show.", confirmLabel: "Remove", tone: "danger" });
    if (!ok) return;
    await api.delete(`/productions/${productionId}/members/${userId}`);
    load();
  };

  return (
    <section className="rounded-lg border border-white/10 bg-[#121212]" data-testid="company-roster">
      <div className="p-5 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Company</div>
          <div className="text-sm text-zinc-300 mt-0.5">{members.length} member{members.length === 1 ? "" : "s"}</div>
        </div>
        {(isAdmin || user.role === "production_staff") && (
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <button data-testid="new-user-btn" style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110 inline-flex items-center gap-1.5"><Plus className="w-4 h-4" />New user</button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-display">Add user to production</DialogTitle>
                <DialogDescription className="text-zinc-500">Create an account and add it as a member of this production.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input data-testid="new-user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
                <div>
                  <Label>System role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="new-user-role-select" className="bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10 text-white">
                      {availableRoles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {!isAdmin && (
                    <div className="text-[10px] text-zinc-500 mt-1">Only admins can grant admin or staff.</div>
                  )}
                </div>
                <div className="col-span-2">
                  <Label>Production role <span className="text-zinc-500 font-normal">(drives which dashboard they see)</span></Label>
                  <Select value={form.production_role || "auto"} onValueChange={(v) => setForm({ ...form, production_role: v === "auto" ? "" : v })}>
                    <SelectTrigger data-testid="new-user-production-role-select" className="bg-[#0A0A0A] border-white/10"><SelectValue placeholder="Auto (based on system role)" /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10 text-white max-h-72">
                      <SelectItem value="auto">— Auto (based on system role) —</SelectItem>
                      {productionRoles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Email</Label><Input data-testid="new-user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
                <div><Label>Password</Label><Input data-testid="new-user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
                <div><Label>Character / Note</Label><Input value={form.character} onChange={(e) => setForm({ ...form, character: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
              </div>
              <DialogFooter>
                <button data-testid="save-new-user-btn" onClick={create} style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110">Create user</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="divide-y divide-white/5">
        {members.length === 0 && <div className="p-8 text-center text-sm text-zinc-500">No members yet.</div>}
        {members.map((u) => (
          <div key={u.id} className="p-4 flex items-center gap-4" data-testid={`roster-row-${u.id}`}>
            <Avatar user={u} size="w-10 h-10" refresh={u.avatar_path || 0} />
            <div className="flex-1 min-w-0">
              <div className="text-sm">{u.name} <span className="text-zinc-500 text-xs">· {u.email}</span></div>
              <div className="text-xs text-zinc-500 flex items-center flex-wrap gap-1 mt-0.5">
                {(() => {
                  const raw = u.character || u.production_character || "";
                  const roles = raw.split(" / ").map((s) => s.trim()).filter(Boolean);
                  if (roles.length === 0) return <span>—</span>;
                  return roles.map((r) => (
                    <span key={r} data-testid={`roster-role-pill-${u.id}-${r}`} className="text-[10px] px-1.5 py-0.5 rounded border border-gold/30 bg-gold/5 text-gold uppercase tracking-widest">{r}</span>
                  ));
                })()}
                {u.phone && <span>· {u.phone}</span>}
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-gold">{u.role}</span>
            {(isAdmin || user.role === "production_staff") && (
              <>
                <button data-testid={`edit-user-${u.id}`} onClick={() => setEditUser(u)} title="Edit contact info" className="text-zinc-400 hover:text-gold p-1.5 rounded border border-white/10 hover:border-gold/40 transition-colors"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`del-user-${u.id}`} onClick={() => removeMember(u.id)} title="Remove from production" className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </>
            )}
          </div>
        ))}
      </div>

      {editUser && (
        <EditUserDialog
          user={editUser}
          isAdmin={isAdmin}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); load(); }}
        />
      )}
    </section>
  );
}

function EditUserDialog({ user, isAdmin, onClose, onSaved }) {
  const [productionRoles, setProductionRoles] = useState([]);
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
    character: user.character || user.production_character || "",
    role: user.role || "cast",
    production_role: user.production_role || "",
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const [avatarKey, setAvatarKey] = useState(user.avatar_path || Date.now());
  useEffect(() => {
    if (isAdmin) api.get("/production-roles").then((r) => setProductionRoles(r.data.defaults || [])).catch(() => {});
  }, [isAdmin]);

  const save = async () => {
    setSaving(true);
    try {
      const upd = { name: form.name, email: form.email, phone: form.phone, character: form.character };
      if (isAdmin) {
        if (form.role) upd.role = form.role;
        if (form.password) upd.password = form.password;
        if (form.production_role !== (user.production_role || "")) upd.production_role = form.production_role;
      }
      await api.patch(`/users/${user.id}`, upd);
      toast.success("Updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/users/${user.id}/avatar`, fd);
      toast.success("Headshot updated");
      setAvatarKey(Date.now());
    } catch {
      toast.error("Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit user</DialogTitle>
          <DialogDescription className="text-zinc-500">Update contact info and (admin only) role or password.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Avatar user={{ ...user, avatar_path: user.avatar_path }} size="w-16 h-16" textClass="text-xl" refresh={avatarKey} />
            <label className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full grid place-items-center bg-gold text-black border-2 border-[#121212] cursor-pointer hover:brightness-110">
              <span className="text-[10px]">+</span>
              <input type="file" accept="image/*" className="hidden" data-testid={`edit-user-avatar-${user.id}`} onChange={uploadAvatar} />
            </label>
          </div>
          <div>
            <div className="font-display text-lg">{user.name}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-widest">{user.role}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Full name</Label><Input data-testid="edit-user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          <div><Label>Email</Label><Input data-testid="edit-user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          <div><Label>Phone</Label><Input data-testid="edit-user-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          <div className="col-span-2"><Label>Character / Note</Label><Input data-testid="edit-user-character-input" value={form.character} onChange={(e) => setForm({ ...form, character: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          {isAdmin && (
            <>
              <div>
                <Label>System role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="edit-user-role-select" className="bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Production role</Label>
                <Select value={form.production_role || "auto"} onValueChange={(v) => setForm({ ...form, production_role: v === "auto" ? "" : v })}>
                  <SelectTrigger data-testid="edit-user-production-role-select" className="bg-[#0A0A0A] border-white/10"><SelectValue placeholder="Auto" /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white max-h-72">
                    <SelectItem value="auto">— Auto —</SelectItem>
                    {productionRoles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Reset password <span className="text-zinc-600 text-xs">(optional)</span></Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
            </>
          )}
        </div>
        <DialogFooter>
          <button data-testid="edit-user-save-btn" onClick={save} disabled={saving} style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110 inline-flex items-center gap-1.5">
            <Save className="w-4 h-4" /> Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
